import { Add, Refresh } from '@mui/icons-material';
import { Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { Button, Col, Row, Stack } from 'react-bootstrap';
import useDebounce from '../../../hooks/useDebounce';
import {
  fetchCustomerData,
  fetchDeletedCustomer,
} from '../../../modules/admin';
import { customerDataHeaders, deletedCustomersDataHeaders } from '../../../static-data/projects';
import SearchBar from '../../elements/SearchBar';
import TableComponent from '../../elements/TableComponent';
import CustomerTableActions from './CustomerTableActions';
import CustomerFormModal from './CustomerFormModal';

const CustomerDataTable = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [perPage, setPerPage] = useState(10);
  const debouncedSearchQuery = useDebounce(searchValue, 500);
  const [page, setPage] = useState(1);
  const [order, setOrder] = useState('desc');
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);

  const fetchCustomerList = async () => {
    setLoading(true);
    try {
      let response = {};
      if (showDeleted) {
        response = await fetchDeletedCustomer(
          page,
          perPage,
          debouncedSearchQuery
        );
      } else {
        response = await fetchCustomerData(page, perPage, debouncedSearchQuery);
      }

      setCustomers(response?.data || []);
      setTotalCount(response?.total || 0);
    } catch (error) {
      console.error('Error fetching customer data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomerList();
  }, [debouncedSearchQuery, page, showDeleted, perPage]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearch = (value) => {
    setLoading(true);
    setSearchValue(value);
    setPage(1);
  };

  const handleShowDeleted = () => {
    setShowDeleted(!showDeleted);
    setPage(1);
  };

  const handlePageSizeChange = (e) => {
    const newPerPage = parseInt(e.target.value, 10);
    setPerPage(newPerPage);
    setPage(1);
  };

  return (
    <>
      <Row>
        <Col>
          <Typography variant="h4" className="mb-3">
            Customers
          </Typography>
        </Col>
      </Row>
      <Row className="align-items-center">
        <Col className="d-flex">
          <SearchBar
            value={searchValue}
            onChange={(e) => handleSearch(e.target.value)}
            clearSearch={() => handleSearch('')}
            placeholder="Search Customers"
          />
        </Col>
        <Col className="d-flex justify-content-end">
          <Stack direction="horizontal" gap={2}>
            {!showDeleted && (
              <Button
                variant="primary"
                onClick={() => setShowAddCustomerModal(true)}
                title="Add New Customer">
                <Add className="me-1" /> Add Customer
              </Button>
            )}
            <Button
              variant="outline-secondary"
              onClick={handleShowDeleted}
              title={`Click to show ${
                showDeleted ? 'Existing' : 'Deleted'
              } customers`}>
              {showDeleted
                ? 'Show Existing Customers'
                : 'Show Deleted Customers'}
            </Button>
            <Button
              variant="outline-secondary"
              className="remove-focus"
              onClick={fetchCustomerList}
              title="Click to refresh the list">
              <Refresh color={'#6c757d'} size={'20px'} />
            </Button>
          </Stack>
        </Col>
      </Row>
      <Row>
        <Col>
          <TableComponent
            data={customers || []}
            headerTabs={showDeleted ? deletedCustomersDataHeaders : customerDataHeaders}
            page={page}
            handlePageChange={handlePageChange}
            perPage={perPage}
            order={order}
            setOrder={setOrder}
            defaultSort="customer"
            isLoading={loading}
            striped
            totalCount={totalCount}
            showPages
            tablekey="customers"
            showCount={true}
            showRowCount={true}
            setPerPage={handlePageSizeChange}
            action={(row) => {
              if (showDeleted) return null;
              return (
                <CustomerTableActions
                  row={row}
                  fetchCustomerList={fetchCustomerList}
                  latestExport={row.latest_export || null}
                  updateExportRequest={fetchCustomerList}
                />
              );
            }}
          />
        </Col>
      </Row>
      <CustomerFormModal
        open={showAddCustomerModal}
        setOpen={setShowAddCustomerModal}
        fetchCustomerList={fetchCustomerList}
      />
    </>
  );
};

export default CustomerDataTable;
